import { css } from 'styled-components';

import { utils } from '@desygner/ui-common-components';

import {
  Border,
  Card,
  Grid,
  Icon,
  Placeholder,
  Screen,
  Scrollbar,
  Sizes,
  Spacing,
  Button as ThemeButton,
  Color as ThemeColor,
  Components as ThemeComponents,
  Form as ThemeForm,
  Modal as ThemeModal,
  Transition,
  Typography,
  ZIndex,
} from '../theme';

export {
  Border,
  Card,
  Grid,
  Icon,
  Placeholder,
  Screen,
  Scrollbar,
  Sizes,
  Spacing,
  Transition,
  Typography,
  ZIndex,
};

export const Color = {
  ...ThemeColor,
  System: {
    Primary: '#3249FF',
    Dark: '#1A27BA',
    Light: '#000A76',
    Lightest: '#000650',
    Success: '#49AA19',
  },

  Neutrals: {
    Dark: '#FFFFFF',
    Text: 'rgba(255, 255, 255, 0.88)',
    Gray: 'rgba(255, 255, 255, 0.45)',
    Border: '#333333',
    Light: '#1F1F1F',
    Paper: '#121212',
    Surface: '#2A2A2A',
  },

  Feedback: {
    Alert: '#FF4D4F',
    AlertLight: '#240B0D',
    Success: '#52C41A',
    SuccessLight: '#071602',
    Warning: '#FAAD14',
    WarningLight: '#231808',
    Info: '#2A3EE1',
    InfoLight: '#11132F',
    InfoSpecial: '#536AED',
  },

  Brand: {
    Darkest: '#FFFFFF',
    Primary: '#3249FF',
    Dark: '#1A27BA',
    Light: '#000A76',
    Lightest: '#000650',
    SpecialLight: '#4B2C57',
    Special: '#5E376D',
    SpecialDark: '#BC93D0',
  },

  Overlay: {
    Light: 'rgba(255,255,255,0.18)',
    Default: 'rgba(255, 255, 255, 0.45)',
    Hover: 'rgba(255, 255, 255, 0.85)',
  },
};

export const Components = {
  ...ThemeComponents,
  Empty: {
    ...ThemeComponents.Empty,
    Icon: 'empty-box-black---113',
  },
  Folder: {
    ...ThemeComponents.Folder,
    Color: {
      Background: Color.System.Primary,
      Title: Color.Neutrals.Dark,
      Subtitle: Color.Neutrals.Gray,
    },
    IconWrapper: 'folder-black---46',
  },
  Skeleton: {
    Color: {
      Dark: 'rgba(255, 255, 255, 0.12)',
      Light: 'rgba(255, 255, 255, 0.18)',
    },
    Animation: {
      Gradient: {
        Start: 25,
        Center: 37,
        End: 63,
      },
      Background: 400,
    },

    Section: {
      ...ThemeComponents.Section,
      Icons: {
        Highlight: 'effect-highlight-dark---48',
        Underline: 'effect-underline-dark---48',
        LineThrough: 'effect-line-through-dark---48',
        Stroke: 'effects-stroke-dark---48',
      },
    },
  },

  Tooltip: {
    Color: {
      Background: Color.Neutrals.Surface,
      Text: Color.Neutrals.Text,
    },
  },

  Background: Color.NeutralsFixed.Dark,

  Logo: {
    Color: Color.Neutrals.Dark,
    Size: ThemeComponents.Logo.Size,
  },

  Panel: {
    Background: Color.Neutrals.Light,
    BackgroundHover: Color.Neutrals.Border,
    Divider: {
      Color: Color.Neutrals.Border,
    },
  },

  AiAssistant: {
    ...ThemeComponents.AiAssistant,
    Background: css`
      -webkit-backdrop-filter: blur(10px);
      backdrop-filter: blur(10px);
      background-color: rgba(255, 255, 255, 0.16);
      border: ${utils.rem(Border.Size.Small)} solid rgba(200, 200, 200, 0.16);
    `,
  },
  Ai: {
    Overview: {
      Background: Color.NeutralsFixed.Dark,
    },
    Tools: {
      Background: Color.Neutrals.Border,
    },
    ChatBubble: {
      Background: Color.Neutrals.Paper,
    },
    NewAIChat: {
      Disabled: Color.Neutrals.Border,
    },
  },
};

export const Button = {
  ...ThemeButton,
  Variant: {
    Outline: {
      Color: {
        Background: Color.Neutrals.Paper,
        Border: Color.Neutrals.Border,
        Content: Color.Neutrals.Dark,
      },
      Hover: {
        Background: Color.Neutrals.Paper,
        Content: Color.System.Primary,
      },
    },

    Secondary: {
      Color: {
        Background: Color.Neutrals.Light,
        Content: Color.Neutrals.Dark,
      },
      Hover: {
        Background: Color.Brand.Primary,
        Content: Color.Brand.Primary,
      },
    },
  },
};

export const Form = {
  ...ThemeForm,
  Switch: {
    ...ThemeForm.Switch,
    Color: Color.System.Primary,
  },
  Input: {
    ...ThemeForm.Input,
    Placeholder: {
      Color: Color.Neutrals.Gray,
    },
  },

  Checkbox: {
    Color: {
      Background: Color.Overlay.Light,
    },
  },
};

export const Version = 'dark';

export const Modal = {
  ...ThemeModal,
  OverlayColor: Color.Overlay.Light,
};
